import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  Send,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  RotateCcw,
  Eye,
} from "lucide-react";
import Swal from "sweetalert2";
import { notificacionesService } from "../../services/api";

const ESTADO_CONFIG = {
  ENVIADO: { label: "Enviado", icon: CheckCircle, color: "text-green-600", bg: "bg-green-100" },
  PENDIENTE: { label: "Pendiente", icon: Clock, color: "text-yellow-600", bg: "bg-yellow-100" },
  ERROR: { label: "Error", icon: AlertTriangle, color: "text-red-600", bg: "bg-red-100" },
  FALLIDO: { label: "Fallido", icon: XCircle, color: "text-gray-600", bg: "bg-gray-100" },
  CANCELADO: { label: "Cancelado", icon: XCircle, color: "text-gray-500", bg: "bg-gray-100" },
};

const CANAL_STYLES = {
  EMAIL: "bg-indigo-100 text-indigo-700",
  SMS: "bg-green-100 text-green-700",
  PUSH: "bg-purple-100 text-purple-700",
  WHATSAPP: "bg-emerald-100 text-emerald-700",
};

export default function HistorialNotificaciones() {
  const { user } = useAuth();
  const [notificaciones, setNotificaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ estado_envio: "", canal: "" });
  const [detalle, setDetalle] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    cargarStats();
    cargarNotificaciones();
  }, []);

  useEffect(() => {
    setPage(1);
    cargarNotificaciones(1);
  }, [filters]);

  useEffect(() => {
    cargarNotificaciones(page);
  }, [page]);

  const cargarStats = async () => {
    try {
      const params = user?.empresa_id ? { empresa_id: user.empresa_id } : {};
      const data = await notificacionesService.estadisticas(params);
      setStats(data);
    } catch {}
  };

  const cargarNotificaciones = async (p = page) => {
    setLoading(true);
    try {
      const params = { page: p, per_page: 20 };
      if (filters.estado_envio) params.estado_envio = filters.estado_envio;
      if (filters.canal) params.canal = filters.canal;
      if (user?.empresa_id) params.empresa_id = user.empresa_id;
      const data = await notificacionesService.getAll(params);
      // Estructura: { success, data: { current_page, data: [...] } }
      const paginated = data?.data ?? data;
      const items = Array.isArray(paginated) ? paginated : paginated?.data ?? [];
      setNotificaciones(items);
      if (paginated?.last_page) setTotalPages(paginated.last_page);
    } catch {
      setNotificaciones([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReenviar = async (notif) => {
    const result = await Swal.fire({
      title: "¿Reenviar notificación?",
      text: "Se volverá a intentar el envío.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, reenviar",
      cancelButtonText: "Cancelar",
    });
    if (!result.isConfirmed) return;
    try {
      await notificacionesService.reenviar(notif.id);
      Swal.fire({ icon: "success", title: "Notificación reenviada", toast: true, position: "top-end", showConfirmButton: false, timer: 2500 });
      cargarNotificaciones();
    } catch {
      Swal.fire({ icon: "error", title: "No se pudo reenviar", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return "-";
    return new Date(fecha).toLocaleString("es-PE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Historial de Notificaciones</h1>
        <p className="text-sm text-gray-500 mt-1">Registro de todas las notificaciones generadas por el sistema.</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Total</p>
            <p className="text-2xl font-bold text-gray-800 mt-1">{stats.total ?? "-"}</p>
          </div>
          <div className="bg-white border border-green-200 rounded-xl p-4 shadow-sm">
            <p className="text-xs text-green-600 font-medium uppercase tracking-wide">Enviadas</p>
            <p className="text-2xl font-bold text-green-700 mt-1">{stats.enviadas ?? "-"}</p>
          </div>
          <div className="bg-white border border-yellow-200 rounded-xl p-4 shadow-sm">
            <p className="text-xs text-yellow-600 font-medium uppercase tracking-wide">Pendientes</p>
            <p className="text-2xl font-bold text-yellow-700 mt-1">{stats.pendientes ?? "-"}</p>
          </div>
          <div className="bg-white border border-red-200 rounded-xl p-4 shadow-sm">
            <p className="text-xs text-red-600 font-medium uppercase tracking-wide">Errores</p>
            <p className="text-2xl font-bold text-red-700 mt-1">{stats.errores ?? "-"}</p>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 mb-5">
        <select
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={filters.estado_envio}
          onChange={(e) => setFilters((f) => ({ ...f, estado_envio: e.target.value }))}
        >
          <option value="">Todos los estados</option>
          <option value="ENVIADO">Enviado</option>
          <option value="PENDIENTE">Pendiente</option>
          <option value="ERROR">Error</option>
          <option value="FALLIDO">Fallido</option>
          <option value="CANCELADO">Cancelado</option>
        </select>

        <select
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={filters.canal}
          onChange={(e) => setFilters((f) => ({ ...f, canal: e.target.value }))}
        >
          <option value="">Todos los canales</option>
          <option value="EMAIL">Email</option>
          <option value="SMS">SMS</option>
          <option value="PUSH">Push</option>
          <option value="WHATSAPP">WhatsApp</option>
        </select>

        <button
          onClick={() => cargarNotificaciones()}
          className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
        >
          <RefreshCw size={14} /> Actualizar
        </button>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        {loading ? (
          <div className="py-20 text-center text-gray-400 text-sm">Cargando historial...</div>
        ) : notificaciones.length === 0 ? (
          <div className="py-20 text-center">
            <Send size={36} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 text-sm">No hay notificaciones registradas.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Fecha</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Evento</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Canal</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Destinatario</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Estado</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Intentos</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {notificaciones.map((n) => {
                const estado = ESTADO_CONFIG[n.estado_envio] || ESTADO_CONFIG.PENDIENTE;
                const EstadoIcon = estado.icon;
                return (
                  <tr key={n.id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatFecha(n.fecha_creacion || n.created_at)}</td>
                    <td className="px-4 py-3 text-gray-700 font-mono text-xs">
                      {n.regla?.tipo_evento?.replace(/_/g, " ") || "-"}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${CANAL_STYLES[n.canal] || "bg-gray-100 text-gray-600"}`}>
                        {n.canal}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 truncate max-w-[160px]">{n.destinatario}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${estado.bg} ${estado.color}`}>
                        <EstadoIcon size={11} /> {estado.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-500">{n.intentos ?? 0}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setDetalle(n)}
                          title="Ver detalle"
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition"
                        >
                          <Eye size={15} />
                        </button>
                        {n.estado_envio === "ERROR" && (
                          <button
                            onClick={() => handleReenviar(n)}
                            title="Reenviar"
                            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition"
                          >
                            <RotateCcw size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition"
          >
            Anterior
          </button>
          <span className="text-sm text-gray-600">Página {page} de {totalPages}</span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition"
          >
            Siguiente
          </button>
        </div>
      )}

      {/* Modal detalle */}
      {detalle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setDetalle(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 m-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">Detalle de notificación</h3>
              <button onClick={() => setDetalle(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="space-y-3 text-sm">
              <div><span className="font-medium text-gray-600">Destinatario:</span> <span className="text-gray-800">{detalle.destinatario}</span></div>
              <div><span className="font-medium text-gray-600">Canal:</span> <span className="text-gray-800">{detalle.canal}</span></div>
              <div><span className="font-medium text-gray-600">Estado:</span> <span className="text-gray-800">{detalle.estado_envio}</span></div>
              <div><span className="font-medium text-gray-600">Intentos:</span> <span className="text-gray-800">{detalle.intentos}</span></div>
              {detalle.fecha_envio && (
                <div><span className="font-medium text-gray-600">Enviado:</span> <span className="text-gray-800">{formatFecha(detalle.fecha_envio)}</span></div>
              )}
              {detalle.error_mensaje && (
                <div>
                  <span className="font-medium text-red-600">Error:</span>
                  <pre className="mt-1 bg-red-50 text-red-700 text-xs p-3 rounded-lg whitespace-pre-wrap">{detalle.error_mensaje}</pre>
                </div>
              )}
              <div>
                <span className="font-medium text-gray-600">Mensaje:</span>
                <pre className="mt-1 bg-gray-50 text-gray-700 text-xs p-3 rounded-lg whitespace-pre-wrap">{detalle.mensaje}</pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
